/* eslint-disable camelcase */
import { _ } from "~/i18n";
import { ICreateLikeData, ICreateLikeInputData, IMapyBoxItem, TMapyBoxType } from "~/interfaces";
import { store } from "~/state/store";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";
import { getCriterion, getRouteSubtitle } from "./route";
import { timeWithUserSpeed } from "./user-speed";
import { standardDistanceFormat } from "./format";
import { folderUpdate, likeUpdate, trackUpdate } from "./providers/mapybox";

type TOperation = "edit" | "folder-create" | "folder-copy" | "track-update" | "track-copy" | "track-link" | "move-items" | "like-copy" | "delete" | "move-track-link-conflict" | "gpx-import";

const EMAIL_REGEXP = /[+-_!#$%&'*=?{|}~^/a-z0-9]+(\.[+-_!#$%&'*=?{|}~^/a-z0-9]+)*(\.*@seznam|\.*@email|\.*@post|\.*@spoluzaci|\.*@firmy|\.*@stream|@[a-z0-9-]+)(\.[a-z0-9-]+)*(\.[a-z-]{2,20})/igu;
const PHONE_REGEXP = /([+]\d{2,3}[ ]?)?[1-9][0-9]{2}[ ]?\d{3}[ ]?\d{3,4}/ug;
const MYMAPS_DURATION = 4000;

function stripLinkHtml(value: string) {
	return value.replace(/<a.*<\/a>/ug, "");
}

// route: data.routeItems,
// source_id: data.source_id, misto id
export function getCreateLikeData(data: ICreateLikeInputData, type: TMapyBoxType, folderId?: string) {
	// @ts-ignore
	let likeData: ICreateLikeData = {};
	let coords = null;
	const title = data.title.trim();
	const description = (data.description || "").trim();

	switch (type) {
		case "point":
			coords = data.coords.toWGS84();

			likeData = {
				type,
				title,
				description,
				mark: {
					lat: coords[1],
					lon: coords[0],
				},
			};
			break;

		case "entity":
			coords = data.coords.toWGS84();

			likeData = {
				type,
				title,
				description,
				source: data.source,
				source_id: data.source_id,
				mark: {
					lat: coords[1],
					lon: coords[0],
				},
			};
			break;

		case "measure":
			likeData = {
				type,
				title,
				description,
				data: {
					points: data.points,
				},
			};

			if (data.readonly) {
				likeData.data.readonly = data.readonly;
			}

			if ("tid" in data) {
				likeData.data.tid = data.tid;
			}
			break;

		case "path":
			likeData = {
				data: {
					data: [{
						route: data.route,
					}],
				},
				title: title || data.title,
				usertitle: title || data.title,
				description,
				trackType: data.trackType || "pesky",
				type: "path",
			};
			break;

		// uzivatelske znacky
		case "set":
			likeData = {
				type,
				title,
				description,
				data: {
					uc: data.uc,
					ud: data.ud,
					ut: data.ut,
				},
			};
			break;

		case "route":
			likeData = {
				type,
				title,
				description,
				data: {
					route: data.routeItems,
					totalLength: data.totalLength,
					totalTime: data.totalTime,
					geometry: data.geometry,
					routeParams: data.routeParams,
				},
			};
			break;

		// neni v db, specialni pro presun
		case "move":
			likeData = {
				title,
				description,
				ids: data.ids,
				actualFolderId: data.actualFolderId,
			};
			break;

		case "rename-move":
			likeData = {
				title,
				description,
				ids: data.ids,
				actualFolderId: data.actualFolderId,
			};
			break;

		// neni v db, specialni pro kopirovani
		case "copy":
			likeData = {
				id: data.id,
				title,
				description,
				type: data.type,
			};
			break;

		case "track":
			likeData = {
				title,
				description,
				id: data.id,
			};
			break;

		default:
	}

	if (data.subtitle) {
		likeData.subtitle = data.subtitle;
	}

	if (data.usertitle) {
		likeData.usertitle = data.usertitle;
	}

	// folder id
	if (folderId) {
		likeData.parent_id = folderId;
	}

	return likeData;
}

type TMyMamypboxType = "track" | "route" | "path" | "usermarks" | "measure" | "place" | "folder" | "none";

export function getMapyboxMyType(item: IMapyBoxItem): TMyMamypboxType {
	if (!item) {
		return "none";
	}

	let output: TMyMamypboxType = "place";

	switch (item.type) {
		case "folder":
		case "route":
		case "path":
		case "measure":
			output = item.type;
			break;

		default:
			if (item.type === "set") {
				output = "usermarks";
			} else if (item.linktype === "track") {
				output = "track";
			} else {
				output = "place";
			}
	}

	return output;
}

interface IMapyboxNotification {
	operation: TOperation;
	item?: IMapyBoxItem;
	folderTitle?: string;
	folderId?: string;
	moveItemsLen?: number;
	// mazu vice id
	removeItemsLen?: number;
	// remove - pro tracky
	isRemove?: boolean;
}

export function mapyboxNotification({
	item,
	operation = "edit",
	folderId = "",
	folderTitle = "",
	moveItemsLen = 0,
	removeItemsLen = 0,
	isRemove = false,
}: IMapyboxNotification) {
	let msg = "";
	let appendFolder = false;
	let type: NOTIFICATIONS_TYPE = NOTIFICATIONS_TYPE.SUCCESS;
	const rootId = store.getState().mymaps.rootId;
	const isRootFolder = !folderId || (rootId && rootId === folderId);
	const curFolderId = isRootFolder
		? rootId
		: folderId;
	const curTitleFolder = isRootFolder
		? _("detail.content.mymapsplace.saved2")
		: folderTitle;

	if (operation === "edit" || operation === "track-update") {
		msg = _("mymap.notifications.edit.success");
	} else if (operation === "move-track-link-conflict") {
		// Aktivita již existuje ve složce. {FOLDER klikaci}
		type = NOTIFICATIONS_TYPE.INFO;
		appendFolder = true;
		msg = _("mymap.addPopup.tracklinkConflict");
	} else if (operation === "move-items" && moveItemsLen > 0) {
		// hromadne operace
		appendFolder = true;
		msg = isRootFolder
			? _("mymap.notifications.multiple.move.saved", { count: moveItemsLen })
			: _("mymap.notifications.multiple.move.folder", { count: moveItemsLen });
	} else if (operation === "gpx-import") {
		appendFolder = true;
		msg = isRootFolder
			? _("mymap.notifications.gpxImport.save.saved")
			: _("mymap.notifications.gpxImport.save.folder");
	} else if (operation === "delete" && removeItemsLen > 0) {
		// hromadne operace
		msg = _("mymap.notifications.multiple.delete", { count: removeItemsLen });
	} else if (operation === "folder-create") {
		msg = _("mymap.notifications.folderCreate");
	} else if (operation === "folder-copy") {
		msg = _("mymap.notifications.folderCreate");
	} else if (operation === "track-link" || operation === "track-copy") {
		// link aktivity + kopie k sobe do batuzku
		appendFolder = true;
		msg = isRootFolder
			? _("mymap.notifications.track.link.saved")
			: _("mymap.notifications.track.link.folder");
	} else {
		const myType = getMapyboxMyType(item);

		switch (myType) {
			case "place":
				switch (operation) {
					case "like-copy":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.place.save.saved")
							: _("mymap.notifications.place.save.folder");
						break;

					case "move-items":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.place.move.saved")
							: _("mymap.notifications.place.move.folder");
						break;

					case "delete":
						msg = _("mymap.notifications.place.delete");
						break;

					default:
				}
				break;

			case "route":
				switch (operation) {
					case "like-copy":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.route.save.saved")
							: _("mymap.notifications.route.save.folder");
						break;

					case "move-items":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.route.move.saved")
							: _("mymap.notifications.route.move.folder");
						break;

					case "delete":
						msg = _("mymap.notifications.route.delete");
						break;

					default:
				}
				break;

			case "track":
				switch (operation) {
					case "move-items":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.track.move.saved")
							: _("mymap.notifications.track.move.folder");
						break;

					case "delete":
						msg = isRemove
							? _("mymap.notifications.track.remove")
							: _("mymap.notifications.track.delete");
						break;

					default:
				}
				break;

			case "usermarks":
				switch (operation) {
					case "like-copy":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.usermarks.save.saved")
							: _("mymap.notifications.usermarks.save.folder");
						break;

					case "move-items":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.usermarks.move.saved")
							: _("mymap.notifications.usermarks.move.folder");
						break;

					case "delete":
						msg = _("mymap.notifications.usermarks.delete");
						break;

					default:
				}
				break;

			case "measure":
				switch (operation) {
					case "like-copy":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.distanceMeter.save.saved")
							: _("mymap.notifications.distanceMeter.save.folder");
						break;

					case "move-items":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.distanceMeter.move.saved")
							: _("mymap.notifications.distanceMeter.move.folder");
						break;

					case "delete":
						msg = _("mymap.notifications.distanceMeter.delete");
						break;

					default:
				}
				break;

			case "path":
				switch (operation) {
					case "like-copy":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.path.save.saved")
							: _("mymap.notifications.path.save.folder");
						break;

					case "move-items":
						appendFolder = true;
						msg = isRootFolder
							? _("mymap.notifications.path.move.saved")
							: _("mymap.notifications.path.move.folder");
						break;

					case "delete":
						msg = _("mymap.notifications.path.delete");
						break;

					default:
				}
				break;

			case "folder":
				switch (operation) {
					case "delete":
						msg = _("mymap.notifications.folderDelete");
						break;

					default:
				}
				break;

			default:
		}
	}

	if (!msg) {
		/* eslint-disable-next-line */
		console.log(["empty msg", { item, operation, folderId, folderTitle, moveItemsLen, removeItemsLen, isRemove }]);

		return;
	}

	const content = Mapy.Util.node("p", { className: "addpopup-notification", textContent: msg });

	if (appendFolder) {
		const folderEl = Mapy.Util.node("span", { className: "folder-name", textContent: ` ${curTitleFolder}` });

		folderEl.setAttribute("data-folder-id", curFolderId);
		folderEl.addEventListener("click", () => {
			Mapy.getComponent("mymaps").setState({
				mid: curFolderId,
				cat: "mista-trasy",
			});

			if (Mapy.getComponent("mymaps").activate()) {
				Mapy.getComponent("mymaps").deactivate();
			}

			Mapy.getComponent("mymaps").activate();
			Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("places"));
			Mapy.getComponent("history").save();
		});
		content.appendChild(folderEl);
	}

	Notifications.notify({
		content,
		type,
		noClose: true,
		duration: MYMAPS_DURATION,
	});
}

export function getMapyboxError({
	item,
	operation = "edit",
	moveItemsLen = 0,
	removeItemsLen = 0,
	isRemove = false,
}: IMapyboxNotification) {
	let errorMsg = "";
	const myType = getMapyboxMyType(item);

	if (operation === "edit" || operation === "track-update") {
		errorMsg = _("mymap.notifications.edit.error");
	} else if (operation === "gpx-import") {
		errorMsg = _("mymap.notifications.gpxImport.save.error");
	} else if (operation === "folder-create") {
		errorMsg = _("mymap.notifications.folderCreate.error");
	} else if (operation === "move-items" && moveItemsLen > 0) {
		// hromadne operace
		errorMsg = _("mymap.notifications.multiple.move.error", { count: moveItemsLen });
	} else if (operation === "delete" && removeItemsLen > 0) {
		// hromadne operace
		errorMsg = _("mymap.notifications.multiple.delete.error", { count: removeItemsLen });
	} else if (operation === "folder-copy") {
		errorMsg = _("mymap.notifications.folderCreate.error");
	} else if (operation === "track-link" || operation === "track-copy") {
		// link aktivity + kopie k sobe do batuzku
		errorMsg = _("mymap.notifications.track.link.error");
	} else {
		switch (myType) {
			case "place":
				switch (operation) {
					case "like-copy":
						errorMsg = _("mymap.notifications.place.save.error");
						break;

					case "move-items":
						errorMsg = _("mymap.notifications.place.move.error");
						break;

					case "delete":
						errorMsg = _("mymap.notifications.place.delete.error");
						break;

					default:
				}
				break;

			case "route":
				switch (operation) {
					case "like-copy":
						errorMsg = _("mymap.notifications.route.save.error");
						break;

					case "move-items":
						errorMsg = _("mymap.notifications.route.move.error");
						break;

					case "delete":
						errorMsg = _("mymap.notifications.route.delete.error");
						break;

					default:
				}
				break;

			case "track":
				switch (operation) {
					case "move-items":
						errorMsg = _("notifications.track.move.error");
						break;

					case "delete":
						errorMsg = isRemove
							? _("mymap.notifications.track.remove.error")
							: _("mymap.notifications.track.delete.error");
						break;

					default:
				}
				break;

			case "usermarks":
				switch (operation) {
					case "like-copy":
						errorMsg = _("mymap.notifications.usermarks.save.error");
						break;

					case "move-items":
						errorMsg = _("mymap.notifications.usermarks.move.error");
						break;

					case "delete":
						errorMsg = _("mymap.notifications.usermarks.delete.error");
						break;

					default:
				}
				break;

			case "measure":
				switch (operation) {
					case "like-copy":
						errorMsg = _("mymap.notifications.distanceMeter.save.error");
						break;

					case "move-items":
						errorMsg = _("mymap.notifications.distanceMeter.move.error");
						break;

					case "delete":
						errorMsg = _("mymap.notifications.distanceMeter.delete.error");
						break;

					default:
				}
				break;

			case "path":
				switch (operation) {
					case "like-copy":
						errorMsg = _("mymap.notifications.path.save.error");
						break;

					case "move-items":
						errorMsg = _("mymap.notifications.path.move.error");
						break;

					case "delete":
						errorMsg = _("mymap.notifications.path.delete.error");
						break;

					default:
				}
				break;

			case "folder":
				switch (operation) {
					case "delete":
						errorMsg = _("mymap.notifications.folderDelete.error");
						break;

					default:
				}
				break;

			default:
		}
	}

	if (!errorMsg) {
		/* eslint-disable-next-line */
		console.log(["empty msg", { item, operation, moveItemsLen, removeItemsLen, isRemove }]);
		errorMsg = _("detail.content.review2.remove.wasError");
	}

	return errorMsg;
}

export function getMapyboxExcError(error: Error) {
	const content = Mapy.Util.node("p", { className: "addpopup-notification", textContent: error.message });

	Notifications.notify({
		content,
		type: NOTIFICATIONS_TYPE.ERROR,
		noClose: true,
	});
}

export function isPoiSaved(source: string, id: number) {
	return Mapy.getComponent("places").isSaved({
		source,
		id,
	});
}

function applyRegex(description: string, regex: any, replaceCb: (oldValue: string) => string) {
	let updatedDescription = description;
	// budeme testovat text ale bez odkazu, abysme si je neprepisovali
	const matches = stripLinkHtml(description).match(regex);

	if (matches) {
		for (let ind = 0, max = matches.length; ind < max; ind++) {
			const matchedValue = matches[ind];

			updatedDescription = updatedDescription.replace(matchedValue, replaceCb(matchedValue));
		}
	}

	return updatedDescription;
}

function getLink(url: string) {
	const fullUrl = url.includes("http") ? url : `https://${url}`;

	return `<a href="${fullUrl}" target="_blank" rel="noopener">${fullUrl}</a>`;
}

export function getHTMLDescription(description: string): string {
	// smazeme tagy
	const newDescription = applyRegex(description, /[<][^>]+[>]/ug, () => "");
	const lines = newDescription.split("\n");
	const output: Array<string> = [];

	for (const line of lines) {
		let newLine = line;

		// nahradime url - http/https + www
		newLine = applyRegex(newLine, /http[^ \t\n]+/ug, url => getLink(url));
		newLine = applyRegex(newLine, /www\.[^ \t\n]+/ug, url => getLink(url));

		// emaily
		newLine = applyRegex(newLine, EMAIL_REGEXP, email => `<a href="mailto:${email}">${email}</a>`);
		// telefon
		newLine = applyRegex(newLine, PHONE_REGEXP, phone => `<a href="tel:${phone}">${phone}</a>`);

		output.push(newLine);
	}

	return output.join("\n");
}

export function mymapsOpenFolder(folderId: string) {
	// prechod do slozky
	Mapy.getComponent("mymaps").setState({
		mid: folderId,
		cat: "mista-trasy",
	});

	if (Mapy.getComponent("mymaps").activate()) {
		Mapy.getComponent("mymaps").deactivate();
	}

	Mapy.getComponent("mymaps").activate();
	Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("places"));

	Mapy.getComponent("history").save();
}

export function mymapsOpenActivities() {
	Mapy.getComponent("mymaps").setState({
		mid: "",
		cat: "aktivity",
	});

	if (Mapy.getComponent("mymaps").activate()) {
		Mapy.getComponent("mymaps").deactivate();
	}

	Mapy.getComponent("mymaps").activate();
	Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("places"));

	Mapy.getComponent("history").save();
}

export function getMapyboxItemDesc(item: IMapyBoxItem) {
	switch (item.type) {
		case "route":
			if (item.data) {
				return getRouteSubtitle(item.data.totalLength, timeWithUserSpeed(item.data.totalTime, getCriterion()));
			}
			break;

		case "measure": {
			const points = SMap.Coords.stringToCoords(item.data.points);
			let distance = 0;

			for (let ind = 0; ind < points.length - 1; ind++) {
				distance += points[ind].distance(points[ind + 1]);
			}

			const sectionCount = points.length - 1;

			return _(item.data.readonly ? "distanceMeter.module.readonly.subtitle" : "distanceMeter.module.subtitle", { count: sectionCount, section: sectionCount, distance: standardDistanceFormat(distance.toString()).string });
		}

		default:
	}

	return item.subtitle;
}

export function getUserTime(mapyboxItem: Partial<IMapyBoxItem>) {
	// uzivatelem drive zadane datum
	if (typeof mapyboxItem.user_time === "number" && mapyboxItem.user_time > 0) {
		return mapyboxItem.user_time;
	}

	// obsahuje data
	if (mapyboxItem.data) {
		// startovaci cas aktivity
		if (typeof mapyboxItem.data?.info?.startTime === "number") {
			return mapyboxItem.data.info.startTime * 1000;
		}

		// datum startu trasy
		if (typeof mapyboxItem.data?.route?.[0]?.routeParams?.datetime === "number") {
			return new Date(mapyboxItem.data.route[0].routeParams.datetime).getTime();
		}
	}

	// datum vytvoreni oblibene polozky
	if (typeof mapyboxItem.create_time === "number" && mapyboxItem.create_time > 0) {
		return mapyboxItem.create_time * 1000;
	}

	// kdyby fakt nic, vyplnime dnesek a ted
	return Date.now();
}

export function hasVisitedOption(mapyboxItem: IMapyBoxItem) {
	return mapyboxItem && (["entity", "point", "path", "route", "track"].includes(mapyboxItem.type) || mapyboxItem.linktype === "track");
}

interface IUpdateData {
	description?: string;
	public?: boolean;
	// neni pro slozku
	visited?: boolean;
}

/**
 * Neni cilem mit pro vsechno, spis obal pro par veci, ktere se daji upravit vsem a nemuset vsude delat rozcestnik
 */
export function updateMapyBoxItem(mapyboxItem: IMapyBoxItem, updateData: IUpdateData) {
	if (mapyboxItem.type === "track") {
		return trackUpdate(mapyboxItem.id, updateData);
	} else if (mapyboxItem.type === "folder") {
		const { visited, ...restData } = updateData;

		return folderUpdate(mapyboxItem.id, restData);
	}

	return likeUpdate(mapyboxItem.id, updateData);
}

interface IAnalyticsSave {
	type: "poi" | "route" | "activity" | "measure" | "path" | "set" | "folder";
	visited?: boolean;
	note?: boolean;
}

export function getMapyboxItemAnalyticsType(item: IMapyBoxItem): IAnalyticsSave["type"] {
	if (!item) {
		return "poi";
	}

	switch (item.type) {
		case "entity":
		case "point":
			return "poi";

		case "folder":
			return "folder";

		case "measure":
			return "measure";

		case "path":
			return "path";

		case "route":
			return "route";

		case "set":
			return "set";

		case "track":
			return "activity";

		default:
			if (item.linktype === "track") {
				return "activity";
			}

			return "poi";
	}
}

export function mapyboxAnalyticsSave({
	type,
	visited,
	note,
}: IAnalyticsSave) {
	Mapy.getComponent("logger").log("my_maps_item_save", {
		type,
		visited: visited ? 1 : 0,
		note_added: note ? 1 : 0,
	});
}

export interface IAnalyticsEdit {
	type: IAnalyticsSave["type"];
	visited?: boolean;
	note?: boolean;
	changed?: Array<"folder" | "name" | "note" | "visited" | "track_type" | "mood">;
	origin: "detail_box" | "detail_box_edit";
	alwaysOnMap?: boolean;
}

export function mapyboxAnalyticsEdit({
	type,
	visited = undefined,
	note = undefined,
	changed = [],
	origin = "detail_box",
	alwaysOnMap = false,
}: IAnalyticsEdit) {
	Mapy.getComponent("logger").log("my_maps_item_edit", {
		action: "my_maps_item_edit",
		type,
		origin,
		...typeof visited === "boolean"
			? {
				visited: visited ? 1 : 0,
			}
			: {},
		...typeof note === "boolean"
			? {
				note_added: note ? 1 : 0,
			}
			: {},
		...changed.length > 0
			? {
				changed,
			}
			: {},
		...type === "folder" ? { isAlwaysOnMap: alwaysOnMap ? 1 : 0 } : {},
	});
}
